import { useState, useEffect, useCallback } from 'react';
import { plansAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const LOCATIONS = [
  'Bazin Fiald (Hotel Fiald)',
  'Bazinul Olimpic Bacău',
  'Bazinul EMD Academy',
];

const CATEGORIES = { KIDS: 'Copii', ADULTS: 'Adulți', PERFORMANCE: 'Special & Performanță' };
const CATEGORY_ORDER = { KIDS: 0, ADULTS: 1, PERFORMANCE: 2 };
const BADGES = ['', 'PT', 'BabySwim', 'Nelimitat', 'Popular'];

const emptyForm = {
  location: LOCATIONS[0],
  activity: '',
  category: 'KIDS',
  scope: 'child',
  sessions: '',
  duration_days: 30,
  validity_days: 30,
  price: '',
  note: '',
  badge: '',
  is_active: true,
};

const PlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLocation, setActiveLocation] = useState(LOCATIONS[0]);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletePlanId, setDeletePlanId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await plansAPI.getAllAdmin();
      setPlans(res.data);
    } catch {
      toast.error('Eroare la încărcarea abonamentelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const locationPlans = plans
    .filter(p => p.location === activeLocation)
    .sort((a, b) => (CATEGORY_ORDER[a.category] ?? 99) - (CATEGORY_ORDER[b.category] ?? 99));

  const byCategory = locationPlans.reduce((acc, p) => {
    const cat = p.category || 'ADULTS';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const openCreate = () => {
    setEditingPlan(null);
    setForm({ ...emptyForm, location: activeLocation });
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      location: plan.location,
      activity: plan.activity,
      category: plan.category,
      scope: plan.scope,
      sessions: plan.sessions ?? '',
      duration_days: plan.duration_days,
      validity_days: plan.validity_days,
      price: plan.price,
      note: plan.note || '',
      badge: plan.badge || '',
      is_active: plan.is_active,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      sessions: form.sessions === '' ? null : parseInt(form.sessions),
      price: parseFloat(form.price),
      duration_days: parseInt(form.duration_days),
      validity_days: parseInt(form.validity_days),
    };
    try {
      if (editingPlan) {
        await plansAPI.update(editingPlan.id, payload);
        toast.success('Abonament actualizat');
      } else {
        await plansAPI.create(payload);
        toast.success('Abonament creat');
      }
      setShowModal(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await plansAPI.update(plan.id, { ...plan, sessions: plan.sessions ?? null, is_active: !plan.is_active });
      toast.success(plan.is_active ? 'Abonament dezactivat' : 'Abonament activat');
      fetchPlans();
    } catch {
      toast.error('Eroare');
    }
  };

  const handleDelete = async () => {
    try {
      await plansAPI.delete(deletePlanId);
      toast.success('Abonament șters definitiv');
      setDeletePlanId(null);
      fetchPlans();
    } catch {
      toast.error('Eroare la ștergere');
    }
  };

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Setări Abonamente</h1>
          <p className="text-white/40 text-sm mt-1">Gestionează tipurile de abonamente și prețurile</p>
        </div>
        <button onClick={openCreate} className="btn-primary px-4 py-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
          <Plus size={16} /> Abonament Nou
        </button>
      </div>

      {/* Tabs Locații */}
      <div className="flex flex-wrap gap-2">
        {LOCATIONS.map(loc => (
          <button
            key={loc}
            onClick={() => setActiveLocation(loc)}
            className={`px-4 py-2 text-xs font-black uppercase tracking-widest transition-colors rounded ${
              activeLocation === loc
                ? 'bg-[#6db025] text-black'
                : 'bg-white/5 text-white/40 hover:bg-white/10'
            }`}
          >
            {loc.replace('Bazin ', '').replace('Bazinul ', '')}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#6db025] animate-pulse font-heading text-xs tracking-widest uppercase">Se încarcă...</div>
      ) : locationPlans.length === 0 ? (
        <div className="text-center py-20 text-white/30 font-heading text-xs uppercase tracking-widest">
          Niciun abonament pentru această locație.
          <button onClick={openCreate} className="block mx-auto mt-4 text-[#6db025] hover:underline text-xs">+ Adaugă primul abonament</button>
        </div>
      ) : (
        <div className="space-y-10">
          {Object.entries(byCategory)
            .sort(([a], [b]) => (CATEGORY_ORDER[a] ?? 99) - (CATEGORY_ORDER[b] ?? 99))
            .map(([cat, catPlans]) => (
              <div key={cat} className="space-y-3">
                <div className="flex items-center gap-3 px-1">
                  <h2 className="text-white font-black uppercase tracking-widest text-base sm:text-lg">{CATEGORIES[cat] ?? cat}</h2>
                  <span className="text-white/40 text-sm font-bold tracking-wider">({catPlans.length})</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {catPlans.map(plan => (
                    <div key={plan.id} className={`relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden ${!plan.is_active ? 'opacity-50' : ''}`}>
                      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${plan.is_active ? 'bg-[#6db025]' : 'bg-white/20'}`} />
                      <div className="p-4 pl-5">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-bold text-base uppercase tracking-tight truncate">{plan.activity}</p>
                            <div className="flex items-center gap-1.5 flex-wrap mt-1">
                              {plan.badge && (
                                <span className="text-[10px] font-black uppercase tracking-tighter px-2 py-0.5 bg-[#6db025]/10 text-[#6db025] border border-[#6db025]/20 rounded">{plan.badge}</span>
                              )}
                              {!plan.is_active && (
                                <span className="text-[10px] font-black uppercase tracking-tighter px-2 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded">Inactiv</span>
                              )}
                            </div>
                          </div>
                          <span className="text-[#6db025] font-black text-base whitespace-nowrap flex-shrink-0">{plan.price} Lei</span>
                        </div>

                        <div className="flex flex-wrap gap-3 text-white/50 text-[11px] uppercase font-bold tracking-widest mb-3">
                          <span>{plan.sessions ?? '∞'} ședințe</span>
                          <span>{plan.validity_days} zile valabil</span>
                        </div>

                        <div className="flex items-center justify-end gap-1 pt-2 border-t border-white/[0.04]">
                          <button onClick={() => handleToggleActive(plan)} title={plan.is_active ? 'Dezactivează' : 'Activează'} className="p-2 text-white/40 hover:text-yellow-400 hover:bg-yellow-500/10 transition-colors rounded">
                            {plan.is_active ? <ToggleRight size={18} className="text-[#6db025]" /> : <ToggleLeft size={18} />}
                          </button>
                          <button onClick={() => openEdit(plan)} className="p-2 text-white/40 hover:text-white hover:bg-white/5 transition-colors rounded" title="Editează">
                            <Edit size={18} />
                          </button>
                          <button onClick={() => setDeletePlanId(plan.id)} className="p-2 text-red-500/50 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded" title="Șterge">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Modal Creare/Editare */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto p-0 shadow-2xl">
          <DialogHeader className="p-6 border-b border-white/5 sticky top-0 bg-[#121212] z-10">
            <DialogTitle className="font-heading text-xl text-white uppercase">
              {editingPlan ? 'Modifică Abonament' : 'Abonament Nou'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Locație</label>
                <select value={form.location} onChange={f('location')} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none">
                  {LOCATIONS.map(l => <option key={l} value={l} className="bg-[#0A0A0A]">{l}</option>)}
                </select>
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Denumire Activitate</label>
                <input type="text" value={form.activity} onChange={f('activity')} required placeholder="Ex: Abonament copii 8 ședințe" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none" />
              </div>

              <div className="space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Categorie</label>
                <select value={form.category} onChange={f('category')} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none">
                  {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k} className="bg-[#0A0A0A]">{v}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Aplicabil pentru</label>
                <select value={form.scope} onChange={f('scope')} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none">
                  <option value="child" className="bg-[#0A0A0A]">Copii</option>
                  <option value="adult" className="bg-[#0A0A0A]">Adulți</option>
                  <option value="both" className="bg-[#0A0A0A]">Ambii</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Nr. Ședințe (gol = nelimitat)</label>
                <input type="number" value={form.sessions} onChange={f('sessions')} min="1" placeholder="Nelimitat" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none" />
              </div>

              <div className="space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Prețul (Lei)</label>
                <input type="number" value={form.price} onChange={f('price')} required min="0" step="0.01" placeholder="0.00" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none" />
              </div>

              <div className="space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Valabilitate (zile)</label>
                <input type="number" value={form.validity_days} onChange={f('validity_days')} required min="1" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none" />
              </div>

              <div className="space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Badge (opțional)</label>
                <select value={form.badge} onChange={f('badge')} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none">
                  {BADGES.map(b => <option key={b} value={b} className="bg-[#0A0A0A]">{b || '- Fără badge -'}</option>)}
                </select>
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Notă (opțional)</label>
                <input type="text" value={form.note} onChange={f('note')} placeholder="Detalii suplimentare..." className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none" />
              </div>

              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`flex items-center gap-2 px-5 py-3 font-bold uppercase text-[10px] tracking-widest transition-colors border ${form.is_active ? 'bg-[#6db025]/10 text-[#6db025] border-[#6db025]/20' : 'bg-white/5 text-white/40 border-white/10'}`}
                >
                  {form.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                  {form.is_active ? 'Abonament Activ (vizibil pe site)' : 'Abonament Inactiv (ascuns)'}
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-white/5">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-4 bg-white/5 text-white/40 font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
                <X size={14} /> Anulează
              </button>
              <button type="submit" disabled={saving} className="flex-1 btn-primary py-4 font-black uppercase text-[10px] tracking-widest disabled:opacity-20">
                {saving ? 'Se salvează...' : editingPlan ? 'Salvează Modificările' : 'Creează Abonament'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletePlanId} onOpenChange={() => setDeletePlanId(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-500 uppercase font-heading">Șterge Abonament</AlertDialogTitle>
            <AlertDialogDescription className="text-white/40">Abonamentul va fi șters definitiv. Clienții cu abonamente active de acest tip nu vor fi afectați.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-0 text-white">Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white border-0">Șterge Definitiv</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PlansPage;
